import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Stack, Typography } from '@mui/material'
import { t } from 'i18next'
import React, { useState } from 'react'
import { HiCIcon } from 'src/app/components/icons/icon'
import { FrontendPhase } from '../../../utils/phase'
import { faPlaneArrival, faPlaneDeparture, faHourglassHalf, IconDefinition, faBan, faCheck, faHouseChimneyUser, faBedFront } from '@fortawesome/pro-regular-svg-icons'
import { useTranslation } from 'react-i18next'
import { Button, MenuItem, Popover, Select, Tooltip } from '@vapor/material'
import { ResourceOccupancyFragment } from 'src/api/api.generated'
import { HicIconButton } from 'src/app/components/hic-vapor/button/icon-button'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { UpdateReservedResourceOutput } from 'src/app/hooks/update-reserved-resource/update-reserved-resource.types'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'

export const ReservedResourcePhaseCell = React.memo((props: { phase: FrontendPhase }) => {
  switch (props.phase) {
    case FrontendPhase.NotYetArrived:
      return <PhaseLabel icon={faHourglassHalf} text={t('pms.booking.reservation.frontend_phase.not_yet_arrived')} />
    case FrontendPhase.Arriving:
      return <PhaseLabel icon={faPlaneArrival} text={t('pms.booking.reservation.frontend_phase.arriving')} />
    case FrontendPhase.Home:
      return <PhaseLabel icon={faHouseChimneyUser} text={t('pms.booking.reservation.frontend_phase.home')} />
    case FrontendPhase.NoShow:
      return <PhaseLabel icon={faBan} text={t('pms.booking.reservation.frontend_phase.no_show')} />
    case FrontendPhase.Departing:
      return <PhaseLabel icon={faPlaneDeparture} text={t('pms.booking.reservation.frontend_phase.departing')} />
    case FrontendPhase.Departed:
      return <PhaseLabel icon={faCheck} text={t('pms.booking.reservation.frontend_phase.departed')} />
  }
})

const PhaseLabel = (props: { text: string; icon: IconDefinition }) => {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
      <HiCIcon>
        <FontAwesomeIcon icon={props.icon} style={{ width: '18px' }} />
      </HiCIcon>
      <Box component="p" sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', width: '100%' }}>
        <Typography variant="body">{props.text}</Typography>
      </Box>
    </Stack>
  )
}

export const ResourceTypeCell = React.memo((props: { requestResourceType: string; assignedResourceType: string }) => {
  const { t } = useTranslation()
  return (
    <div>
      <Tooltip
        title={
          <>
            {`${props.requestResourceType} (${t('pms.commons.request')})`}
            <br />
            <strong>{`${props.assignedResourceType} (${t('pms.commons.assigned')})`}</strong>
          </>
        }
      >
        <Stack>
          <Typography variant="caption">{`${props.requestResourceType} (${t('pms.commons.request')})`}</Typography>
          <Typography variant="caption">
            <strong>{props.assignedResourceType}</strong>
            {` (${t('pms.commons.assigned')})`}
          </Typography>
        </Stack>
      </Tooltip>
    </div>
  )
})

const NO_LAYOUT = 'NO_LAYOUT'
export type SubmitReservedResourceLayoutIdProps = {
  reservedResourceId: string
  layoutId: string | null
  resourceOccupancyStart: Date
  resourceOccupancyEnd: Date
}

export function ReservedResourceGuestCount({
  currentResourceOccupancy,
  guestCount,
  reservedResourceId,
  onSubmit,
}: {
  currentResourceOccupancy: ResourceOccupancyFragment
  guestCount: string
  reservedResourceId: string
  onSubmit: (props: SubmitReservedResourceLayoutIdProps) => Promise<UpdateReservedResourceOutput | undefined>
}) {
  const { t } = useTranslation()
  const localizeString = useLocalizeString()
  const showError = useErrorSnackBar()

  const assignedResourceType = currentResourceOccupancy.assignedResourceType
  const reservedResourceLayoutId = currentResourceOccupancy.reservedResourceLayoutId
  const defaultLayoutId = reservedResourceLayoutId ?? NO_LAYOUT

  const [layoutId, setLayoutId] = useState<string>(defaultLayoutId)
  const [editLayoutResourceEl, setEditLayoutResourceEl] = useState<HTMLButtonElement | null>(null)

  return (
    <>
      {guestCount}
      {Boolean(assignedResourceType?.configurationsLayout?.length) && (
        <HicIconButton
          sx={{ minWidth: '0px!important', minHeight: '0px!important', marginLeft: 2 }}
          onClick={(event) => {
            setEditLayoutResourceEl(event.currentTarget)
            setLayoutId(defaultLayoutId)
          }}
        >
          <FontAwesomeIcon icon={faBedFront} />
        </HicIconButton>
      )}
      {editLayoutResourceEl && (
        <Popover
          anchorEl={editLayoutResourceEl}
          open={editLayoutResourceEl !== null}
          onClose={() => {
            setEditLayoutResourceEl(null)
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Stack sx={{ padding: 2 }}>
            <Typography variant="body700">{t('pms.booking.reservation.edit_layout')}</Typography>
            <Select
              value={layoutId}
              size="small"
              sx={{ width: '200px' }}
              onChange={(event) => {
                setLayoutId(event.target.value as string)
              }}
            >
              <MenuItem value={NO_LAYOUT}>{t('pms.booking.planning.resources.no_layout')}</MenuItem>
              {assignedResourceType?.configurationsLayout?.map((layout) => (
                <MenuItem key={layout.id} value={layout.id}>
                  {localizeString(layout.description)}
                </MenuItem>
              ))}
            </Select>
            <Stack direction="row" gap={1} justifyContent="end" marginTop={2}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setEditLayoutResourceEl(null)
                  setLayoutId(defaultLayoutId)
                }}
              >
                {t('commons.close')}
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={async () => {
                  const result = await onSubmit({
                    reservedResourceId: reservedResourceId,
                    layoutId: layoutId === NO_LAYOUT ? null : layoutId,
                    resourceOccupancyStart: currentResourceOccupancy.start,
                    resourceOccupancyEnd: currentResourceOccupancy.end,
                  })
                  if (result?.result === 'SUCCESS') {
                    setEditLayoutResourceEl(null)
                  } else if (result?.result === 'ERROR') {
                    showError(result.error)
                  }
                }}
              >
                {t('commons.apply')}
              </Button>
            </Stack>
          </Stack>
        </Popover>
      )}
    </>
  )
}
